import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { X, FileText, Plus, Minus, Trash2, Search, Calendar, Check, Send, AlertTriangle, Camera } from 'lucide-react';
import { PHOTOBOOTH_DEVIS_OPTIONS } from '../config/photoboothPacks';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PROFILES = [
  { key: 'particulier', label: 'Particulier' },
  { key: 'entreprise', label: 'Entreprise' },
  { key: 'association', label: 'Association' },
  { key: 'ce', label: 'CE' },
  { key: 'groupe', label: 'Groupe' },
];
const EVENT_TYPES = [
  '', 'anniversaire_enfant', 'anniversaire_adulte', 'mariage', 'bapteme',
  'communion', 'fete_privee', 'entreprise', 'animation_commerciale',
];

const inputStyle = {
  width: '100%', padding: '9px 11px', borderRadius: 9, fontSize: 13,
  background: 'var(--admin-surface2)', border: '1px solid var(--admin-border)',
  color: 'var(--admin-text)', outline: 'none', boxSizing: 'border-box',
};
const label = { fontSize: 11.5, fontWeight: 600, color: 'var(--admin-muted)', marginBottom: 4, display: 'block' };

function tarifOf(p, profile) {
  const t = p.tarifs || {};
  return t[profile] ?? t.particulier ?? 0;
}

/**
 * Modal de création d'un devis pour un client.
 * Le sélecteur de produits n'affiche comme disponibles que les articles
 * (location / prestation) réellement libres à la date choisie (anti-doublon).
 * Props : client (optionnel, pré-remplit), onClose(), onCreated(quote)
 */
export default function DevisCreateModal({ client, onClose, onCreated }) {
  const token = useMemo(
    () => sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth'),
    []
  );

  // ── Client ────────────────────────────────────────────────────────────────
  const [clientInfo, setClientInfo] = useState({
    client_id: client?.id || null,
    client_nom: client?.nom || '',
    client_prenom: client?.prenom || '',
    client_email: client?.email || '',
    client_telephone: client?.telephone || '',
    client_profile: client?.profile || 'particulier',
  });
  const [clientList, setClientList] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(false);
  const locked = !!client; // client imposé depuis la fiche client

  // ── Événement ───────────────────────────────────────────────────────────────
  const [ev, setEv] = useState({
    type_evenement: '', date_evenement: '', heure_debut: '', heure_fin: '',
    date_reprise: '', adresse: client?.adresse || '',
  });

  // ── Produits ────────────────────────────────────────────────────────────────
  const [allProducts, setAllProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [availSet, setAvailSet] = useState(null);   // Set des ids dispo, null = non vérifié
  const [availLoading, setAvailLoading] = useState(false);
  const [tab, setTab] = useState('articles');       // 'articles' | 'consommables'
  const [prodSearch, setProdSearch] = useState('');
  const [showAddPhotobooth, setShowAddPhotobooth] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [sendAfter, setSendAfter] = useState(!!client?.email);

  const H = useMemo(
    () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }),
    [token]
  );

  // Chargement initial : produits + clients (si pas imposé)
  useEffect(() => {
    fetch(`${BACKEND}/api/products?limit=200`, { headers: H })
      .then(r => r.json()).then(d => setAllProducts(d.products || [])).catch(() => {});
    if (!client) {
      fetch(`${BACKEND}/api/clients?limit=200`, { headers: H })
        .then(r => r.json()).then(d => setClientList(d.clients || [])).catch(() => {});
    }
  }, [H, client]);

  // Vérification de disponibilité dès qu'une date d'événement est choisie
  const checkAvailability = useCallback(async (dateStr, dateEnd) => {
    const locProds = allProducts.filter(p => ['location', 'prestation'].includes(p.type));
    if (!dateStr || locProds.length === 0) { setAvailSet(null); return; }
    setAvailLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/booking/check-availability`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_ids: locProds.map(p => p.id),
          date_start: dateStr, date_end: dateEnd || dateStr,
        }),
      });
      const data = await res.json();
      const ok = new Set((data.products || []).filter(p => p.disponible).map(p => p.product_id));
      setAvailSet(ok);
    } catch { setAvailSet(null); }
    setAvailLoading(false);
  }, [allProducts]);

  useEffect(() => {
    if (ev.date_evenement) checkAvailability(ev.date_evenement, ev.date_reprise || ev.date_evenement);
    else setAvailSet(null);
  }, [ev.date_evenement, ev.date_reprise, checkAvailability]);

  // ── Helpers produits ────────────────────────────────────────────────────────
  function isArticle(p) { return ['location', 'prestation'].includes(p.type); }
  function isAvailable(p) {
    if (!isArticle(p)) return true;                 // vente : toujours dispo (stock géré à part)
    if (!ev.date_evenement) return false;           // pas de date → non ajoutable
    if (availSet === null) return false;            // vérif non terminée
    return availSet.has(p.id);
  }

  function addProduct(p) {
    setItems(prev => {
      const found = prev.find(i => i.product_id === p.id);
      const stock = (p.quantite_disponible ?? null);
      if (found) {
        return prev.map(i => {
          if (i.product_id !== p.id) return i;
          let q = i.quantite + 1;
          if (i.stock != null && q > i.stock) q = i.stock;
          return { ...i, quantite: q };
        });
      }
      return [...prev, {
        _key: p.id,
        product_id: p.id, product_nom: p.nom, product_type: p.type,
        quantite: 1, prix_unitaire: tarifOf(p, clientInfo.client_profile),
        images: p.images || [], stock,
      }];
    });
  }
  function setQty(key, delta) {
    setItems(prev => prev.map(i => {
      if (i._key !== key) return i;
      let q = Math.max(1, i.quantite + delta);
      if (i.stock != null && q > i.stock) q = i.stock;
      return { ...i, quantite: q };
    }));
  }
  function removeItem(key) { setItems(prev => prev.filter(i => i._key !== key)); }

  function addLigneLibre() {
    setItems(prev => [...prev, {
      _key: 'libre-' + Date.now(), custom: true, product_id: null,
      product_nom: '', product_type: 'libre', quantite: 1, prix_unitaire: 0, stock: null,
    }]);
  }
  function addPhotoboothOption(opt) {
    setItems(prev => [...prev, {
      _key: 'pb-' + opt.key + '-' + Date.now(), custom: true, product_id: null,
      product_nom: opt.label, product_type: 'photobooth', quantite: 1, prix_unitaire: opt.prix, stock: null,
    }]);
    setShowAddPhotobooth(false);
    toast.success(`${opt.label} ajouté`);
  }
  function updateLigne(key, field, value) {
    setItems(prev => prev.map(i => (i._key === key ? { ...i, [field]: value } : i)));
  }

  const estimate = items.reduce((s, i) => s + (i.prix_unitaire || 0) * (i.quantite || 1), 0);

  const visibleProducts = allProducts.filter(p => {
    if (p.disponible === false) return false; // hors service → non proposable
    const inTab = tab === 'articles' ? isArticle(p) : p.type === 'vente';
    const inSearch = !prodSearch || (p.nom || '').toLowerCase().includes(prodSearch.toLowerCase());
    return inTab && inSearch;
  });

  function pickClient(c) {
    setClientInfo({
      client_id: c.id, client_nom: c.nom || '', client_prenom: c.prenom || '',
      client_email: c.email || '', client_telephone: c.telephone || '',
      client_profile: c.profile || 'particulier',
    });
    if (c.adresse) setEv(prev => ({ ...prev, adresse: prev.adresse || c.adresse }));
    setShowClientPicker(false);
    setClientSearch('');
    if (c.email) setSendAfter(true);
  }

  const customLinesValid = items.every(i => !i.custom || (i.product_nom || '').trim());
  const canSubmit = clientInfo.client_nom.trim() && ev.date_evenement && items.length > 0 && customLinesValid && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    const payload = {
      session: {
        type_evenement: ev.type_evenement || null,
        date_evenement: ev.date_evenement,
        heure_debut: ev.heure_debut || null,
        heure_fin: ev.heure_fin || null,
        date_livraison: ev.date_evenement,
        date_reprise: ev.date_reprise || ev.date_evenement,
        adresse: ev.adresse || null,
        produits: items.filter(i => !i.custom && i.product_id).map(i => ({ product_id: i.product_id, quantite: i.quantite, options: {} })),
        lignes_libres: items.filter(i => i.custom).map(i => ({ nom: (i.product_nom || '').trim(), prix_unitaire: Number(i.prix_unitaire) || 0, quantite: i.quantite })),
        client_profile: clientInfo.client_profile,
      },
      client_id: clientInfo.client_id,
      client_nom: clientInfo.client_nom.trim(),
      client_prenom: clientInfo.client_prenom || null,
      client_email: clientInfo.client_email || null,
      client_telephone: clientInfo.client_telephone || null,
      client_profile: clientInfo.client_profile,
      adresse_livraison: ev.adresse || null,
    };
    try {
      const res = await fetch(`${BACKEND}/api/devis`, {
        method: 'POST', headers: H,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur lors de la création');

      // Envoi optionnel au client
      if (sendAfter && data.client_email) {
        try {
          await fetch(`${BACKEND}/api/devis/${data.id}/envoyer`, { method: 'POST', headers: H });
          toast.success(`Devis ${data.numero} créé et envoyé au client ✉️`);
        } catch {
          toast.success(`Devis ${data.numero} créé (envoi échoué — à renvoyer manuellement)`);
        }
      } else {
        toast.success(`Devis créé : ${data.numero}`);
      }
      onCreated && onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Erreur');
    }
    setSubmitting(false);
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto"
      style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.7)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="devis-create-modal">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="w-full max-w-3xl rounded-2xl"
          style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)' }}>
          {/* Header */}
          <div className="flex items-center justify-between p-5"
            style={{ borderBottom: '1px solid var(--admin-border-soft)' }}>
            <div className="flex items-center gap-2">
              <FileText size={18} style={{ color: 'var(--admin-gold)' }} />
              <h2 className="font-bold text-lg" style={{ color: 'var(--admin-text)' }}>Nouveau devis</h2>
            </div>
            <button onClick={onClose} style={{ color: 'var(--admin-muted)' }} data-testid="devis-create-close">
              <X size={20} />
            </button>
          </div>

          <div className="p-5" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* ── CLIENT ── */}
            <section>
              <p style={{ ...label, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Client</p>
              {locked ? (
                <div className="adm-card" style={{ padding: '12px 14px' }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--admin-text)' }}>
                    {clientInfo.client_prenom} {clientInfo.client_nom}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--admin-muted)' }}>
                    {clientInfo.client_email || '—'} · {clientInfo.client_telephone || '—'} · {clientInfo.client_profile}
                  </p>
                </div>
              ) : (
                <>
                  <button onClick={() => setShowClientPicker(v => !v)} data-testid="devis-pick-client-btn"
                    style={{ ...inputStyle, textAlign: 'left', cursor: 'pointer', display: 'flex',
                      alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: clientInfo.client_nom ? 'var(--admin-text)' : 'var(--admin-faint)' }}>
                      {clientInfo.client_nom
                        ? `${clientInfo.client_prenom} ${clientInfo.client_nom}`.trim()
                        : 'Choisir un client existant…'}
                    </span>
                    <Search size={14} style={{ color: 'var(--admin-faint)' }} />
                  </button>
                  {showClientPicker && (
                    <div className="adm-card" style={{ padding: 8, marginTop: 6, maxHeight: 220, overflowY: 'auto' }}>
                      <input autoFocus placeholder="Rechercher un client…" style={{ ...inputStyle, marginBottom: 6 }}
                        value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                        data-testid="devis-client-search" />
                      {clientList
                        .filter(c => !clientSearch || [c.nom, c.prenom, c.email]
                          .some(v => v?.toLowerCase().includes(clientSearch.toLowerCase())))
                        .slice(0, 30)
                        .map(c => (
                          <button key={c.id} onClick={() => pickClient(c)}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-lg mb-1"
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                              color: 'var(--admin-text)', textAlign: 'left' }}
                            data-testid={`devis-client-option-${c.id}`}>
                            <span style={{ fontSize: 13 }}>{c.prenom} {c.nom}</span>
                            <span style={{ fontSize: 11, color: 'var(--admin-faint)' }}>{c.email}</span>
                          </button>
                        ))}
                      {clientList.length === 0 && (
                        <p style={{ fontSize: 12, color: 'var(--admin-faint)', padding: 8 }}>Aucun client — remplissez les champs ci-dessous.</p>
                      )}
                    </div>
                  )}
                  {/* Saisie manuelle */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                    <input style={inputStyle} placeholder="Prénom" value={clientInfo.client_prenom}
                      data-testid="devis-client-prenom"
                      onChange={e => setClientInfo(p => ({ ...p, client_prenom: e.target.value, client_id: null }))} />
                    <input style={inputStyle} placeholder="Nom *" value={clientInfo.client_nom}
                      data-testid="devis-client-nom"
                      onChange={e => setClientInfo(p => ({ ...p, client_nom: e.target.value, client_id: null }))} />
                    <input style={inputStyle} placeholder="Email" value={clientInfo.client_email}
                      data-testid="devis-client-email"
                      onChange={e => setClientInfo(p => ({ ...p, client_email: e.target.value }))} />
                    <input style={inputStyle} placeholder="Téléphone" value={clientInfo.client_telephone}
                      data-testid="devis-client-tel"
                      onChange={e => setClientInfo(p => ({ ...p, client_telephone: e.target.value }))} />
                  </div>
                </>
              )}
              <div style={{ marginTop: 10 }}>
                <label style={label}>Profil tarifaire</label>
                <select style={inputStyle} value={clientInfo.client_profile} data-testid="devis-client-profile"
                  onChange={e => setClientInfo(p => ({ ...p, client_profile: e.target.value }))}>
                  {PROFILES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
            </section>

            {/* ── ÉVÉNEMENT ── */}
            <section>
              <p style={{ ...label, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Événement</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={label}>Date de l&apos;événement *</label>
                  <input type="date" style={inputStyle} value={ev.date_evenement}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="devis-date-evenement"
                    onChange={e => setEv(p => ({ ...p, date_evenement: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Date de reprise (retour)</label>
                  <input type="date" style={inputStyle} value={ev.date_reprise}
                    min={ev.date_evenement || new Date().toISOString().split('T')[0]}
                    data-testid="devis-date-reprise"
                    onChange={e => setEv(p => ({ ...p, date_reprise: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Heure début</label>
                  <input type="time" style={inputStyle} value={ev.heure_debut}
                    onChange={e => setEv(p => ({ ...p, heure_debut: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Heure fin</label>
                  <input type="time" style={inputStyle} value={ev.heure_fin}
                    onChange={e => setEv(p => ({ ...p, heure_fin: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Type d&apos;événement</label>
                  <select style={inputStyle} value={ev.type_evenement}
                    onChange={e => setEv(p => ({ ...p, type_evenement: e.target.value }))}>
                    {EVENT_TYPES.map(t => <option key={t} value={t}>{t ? t.replace(/_/g, ' ') : '— non précisé —'}</option>)}
                  </select>
                </div>
                <div>
                  <label style={label}>Adresse de livraison</label>
                  <input style={inputStyle} value={ev.adresse} placeholder="Lieu de l'événement"
                    data-testid="devis-adresse"
                    onChange={e => setEv(p => ({ ...p, adresse: e.target.value }))} />
                </div>
              </div>
            </section>

            {/* ── PRODUITS ── */}
            <section>
              <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                <p style={{ ...label, textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Articles & consommables</p>
                {ev.date_evenement && (
                  <span style={{ fontSize: 11, color: availLoading ? 'var(--admin-gold)' : 'var(--admin-teal)',
                    display: 'flex', alignItems: 'center', gap: 4 }} data-testid="devis-avail-status">
                    <Calendar size={11} />
                    {availLoading ? 'Vérification des dispos…'
                      : `Dispos vérifiées pour le ${ev.date_evenement}`}
                  </span>
                )}
              </div>

              {!ev.date_evenement && (
                <div className="adm-card" style={{ padding: '12px 14px', marginBottom: 10 }}>
                  <p style={{ fontSize: 12.5, color: 'var(--admin-gold)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AlertTriangle size={13} /> Choisissez d&apos;abord une date pour voir les articles disponibles.
                  </p>
                </div>
              )}

              {/* Onglets type */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {[{ k: 'articles', l: 'Articles (location / prestation)' }, { k: 'consommables', l: 'Consommables (vente)' }].map(t => (
                  <button key={t.k} onClick={() => setTab(t.k)} data-testid={`devis-tab-${t.k}`}
                    style={{ padding: '7px 12px', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${tab === t.k ? 'var(--admin-teal-line)' : 'var(--admin-border)'}`,
                      background: tab === t.k ? 'var(--admin-teal-dim)' : 'var(--admin-surface)',
                      color: tab === t.k ? 'var(--admin-teal)' : 'var(--admin-muted)' }}>
                    {t.l}
                  </button>
                ))}
              </div>

              {/* Recherche produit */}
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <Search size={13} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-faint)' }} />
                <input style={{ ...inputStyle, paddingLeft: 32 }} placeholder="Rechercher un produit…"
                  value={prodSearch} onChange={e => setProdSearch(e.target.value)} data-testid="devis-product-search" />
              </div>

              {/* Liste catalogue */}
              <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {visibleProducts.slice(0, 60).map(p => {
                  const dispo = isAvailable(p);
                  const article = isArticle(p);
                  return (
                    <div key={p.id} className="adm-card"
                      style={{ padding: '9px 11px', display: 'flex', alignItems: 'center', gap: 10,
                        opacity: dispo ? 1 : 0.55 }}
                      data-testid={`devis-catalogue-item-${p.id}`}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-text)' }}>{p.nom}</p>
                        <p style={{ fontSize: 11, color: 'var(--admin-faint)' }}>
                          {tarifOf(p, clientInfo.client_profile)}€ · {p.type}
                          {article && p.quantite_disponible != null && ` · ${p.quantite_disponible} en stock`}
                        </p>
                      </div>
                      {article && ev.date_evenement && !dispo && !availLoading && (
                        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 7,
                          background: 'rgba(217,75,93,0.12)', color: '#D94B5D' }}>Réservé</span>
                      )}
                      <button onClick={() => addProduct(p)} disabled={!dispo}
                        data-testid={`devis-add-product-${p.id}`}
                        style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                          border: '1px solid var(--admin-teal-line)',
                          background: dispo ? 'var(--admin-teal-dim)' : 'var(--admin-surface2)',
                          color: dispo ? 'var(--admin-teal)' : 'var(--admin-faint)',
                          cursor: dispo ? 'pointer' : 'not-allowed',
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Plus size={14} />
                      </button>
                    </div>
                  );
                })}
                {visibleProducts.length === 0 && (
                  <p style={{ fontSize: 12, color: 'var(--admin-faint)', padding: 10, textAlign: 'center' }}>Aucun produit</p>
                )}
              </div>

              {/* Ligne libre — article personnalisé saisi à la main */}
              <button type="button" onClick={addLigneLibre} data-testid="devis-add-ligne-libre"
                style={{ marginTop: 10, width: '100%', padding: '9px 11px', borderRadius: 8,
                  border: '1px dashed var(--admin-gold-line, rgba(212,175,55,0.4))',
                  background: 'var(--admin-surface)', color: 'var(--admin-gold)',
                  fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Plus size={14} /> Ajouter une ligne libre (article personnalisé)
              </button>

              {/* Packs photo Photobooth — cochables à la demande du client */}
              <button type="button" onClick={() => setShowAddPhotobooth(s => !s)} data-testid="devis-add-photobooth"
                style={{ marginTop: 8, width: '100%', padding: '9px 11px', borderRadius: 8,
                  border: '1px solid rgba(147,133,236,0.35)',
                  background: 'rgba(147,133,236,0.1)', color: '#B3A7F5',
                  fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Camera size={14} /> Ajouter un pack photo Photobooth
              </button>
              {showAddPhotobooth && (
                <div data-testid="devis-photobooth-picker"
                  style={{ marginTop: 8, padding: 10, borderRadius: 10,
                    border: '1px solid rgba(147,133,236,0.35)', background: 'var(--admin-surface)',
                    display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {PHOTOBOOTH_DEVIS_OPTIONS.map(opt => (
                    <button key={opt.key} type="button" onClick={() => addPhotoboothOption(opt)}
                      data-testid={`devis-photobooth-opt-${opt.key}`}
                      style={{ width: '100%', padding: '8px 11px', borderRadius: 8, textAlign: 'left',
                        border: '1px solid var(--admin-border-soft)', background: 'transparent',
                        color: 'var(--admin-text)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>{opt.label}</span>
                        <span style={{ fontSize: 11, color: 'var(--admin-faint)' }}>{opt.description}</span>
                      </span>
                      <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--admin-gold)', flexShrink: 0 }}>{opt.prix}€</span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* ── SÉLECTION ── */}
            {items.length > 0 && (
              <section>
                <p style={{ ...label, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                  Devis ({items.length} ligne{items.length > 1 ? 's' : ''})
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {items.map(i => (
                    <div key={i._key} className="adm-card"
                      style={{ padding: '9px 11px', display: 'flex', alignItems: 'center', gap: 10 }}
                      data-testid={`devis-line-${i._key}`}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {i.custom ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input value={i.product_nom} onChange={e => updateLigne(i._key, 'product_nom', e.target.value)}
                              data-testid={`devis-ligne-nom-${i._key}`} placeholder="Nom de l'article…"
                              style={{ ...inputStyle, flex: 1, padding: '6px 9px', fontSize: 12.5 }} />
                            <input type="number" min="0" step="0.01" value={i.prix_unitaire}
                              onChange={e => updateLigne(i._key, 'prix_unitaire', e.target.value)}
                              data-testid={`devis-ligne-prix-${i._key}`} placeholder="Prix €"
                              style={{ ...inputStyle, width: 78, padding: '6px 9px', fontSize: 12.5 }} />
                          </div>
                        ) : (
                          <>
                            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-text)' }}>{i.product_nom}</p>
                            <p style={{ fontSize: 11, color: 'var(--admin-faint)' }}>{i.prix_unitaire}€ / unité{i.product_type === 'vente' ? ' · consommable' : ''}</p>
                          </>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <button onClick={() => setQty(i._key, -1)} data-testid={`devis-qty-minus-${i._key}`}
                          style={{ width: 26, height: 26, borderRadius: 7, border: '1px solid var(--admin-border)',
                            background: 'var(--admin-surface2)', color: 'var(--admin-muted)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Minus size={12} />
                        </button>
                        <span style={{ minWidth: 22, textAlign: 'center', fontSize: 13, fontWeight: 700, color: 'var(--admin-text)' }}
                          data-testid={`devis-qty-${i._key}`}>{i.quantite}</span>
                        <button onClick={() => setQty(i._key, 1)} data-testid={`devis-qty-plus-${i._key}`}
                          style={{ width: 26, height: 26, borderRadius: 7, border: '1px solid var(--admin-border)',
                            background: 'var(--admin-surface2)', color: 'var(--admin-muted)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Plus size={12} />
                        </button>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--admin-gold)', minWidth: 60, textAlign: 'right' }}>
                        {((Number(i.prix_unitaire) || 0) * i.quantite).toFixed(2)}€
                      </span>
                      <button onClick={() => removeItem(i._key)} data-testid={`devis-remove-${i._key}`}
                        style={{ color: '#D94B5D', background: 'none', border: 'none', cursor: 'pointer' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center" style={{ marginTop: 10, padding: '10px 12px',
                  borderRadius: 10, background: 'var(--admin-surface)' }}>
                  <span style={{ fontSize: 12.5, color: 'var(--admin-muted)' }}>
                    Estimation TTC <span style={{ fontSize: 11, color: 'var(--admin-faint)' }}>(total définitif calculé à la création)</span>
                  </span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--admin-gold)', fontFamily: 'var(--admin-font-display)' }}
                    data-testid="devis-estimate-total">{estimate.toFixed(2)}€</span>
                </div>
              </section>
            )}

            {/* ── ENVOI + SUBMIT ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="send-after" checked={sendAfter}
                onChange={e => setSendAfter(e.target.checked)} data-testid="devis-send-after"
                disabled={!clientInfo.client_email}
                style={{ width: 16, height: 16, accentColor: 'var(--admin-teal)' }} />
              <label htmlFor="send-after" style={{ fontSize: 12.5, color: clientInfo.client_email ? 'var(--admin-muted)' : 'var(--admin-faint)' }}>
                <Send size={11} style={{ display: 'inline', marginRight: 4 }} />
                Envoyer le devis au client par email après création
                {!clientInfo.client_email && ' (email requis)'}
              </label>
            </div>

            <button onClick={submit} disabled={!canSubmit} data-testid="devis-create-submit"
              className="w-full flex items-center justify-center gap-2"
              style={{ padding: '12px 16px', borderRadius: 11, fontSize: 14, fontWeight: 700,
                background: canSubmit ? 'var(--admin-gold)' : 'var(--admin-surface2)',
                color: canSubmit ? '#2a1d05' : 'var(--admin-faint)', border: 'none',
                cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: submitting ? 0.6 : 1 }}>
              <Check size={16} /> {submitting ? 'Création…' : 'Créer le devis'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
